// TanStack Query hooks for the Analytics feature.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  applyDashboardDesign,
  autoLayoutDashboard,
  createChart,
  createDashboard,
  createDataSheetSource,
  deleteSource,
  createRegistryApi,
  createShare,
  createWebhook,
  deleteChart,
  deleteDashboard,
  deleteShare,
  declareGlossaryTerms,
  declareSourceMeaning,
  deleteWebhook,
  fetchChartData,
  fetchSourceMeaning,
  fetchWorkspaceSemantics,
  getChart,
  getComponentCode,
  getDashboard,
  getWebhook,
  introspectApi,
  fetchApiSources,
  listAvailableTypes,
  listCharts,
  listDashboards,
  listShares,
  listWebhooks,
  previewChartData,
  refreshShare,
  rotateWebhookToken,
  saveDashboardDraft,
  updateChart,
  updateDashboard,
  type CreateApiSourceInput,
  type CreateDataSheetInput,
} from '../lib/api';
import type { Chart, Dashboard, GlossaryTerm, SourceMeaningPatch } from '../lib/types';

const KEYS = {
  availableTypes: ['analytics', 'available-types'] as const,
  componentCode: (t: string) => ['analytics', 'component-code', t] as const,
  charts: ['analytics', 'charts'] as const,
  chartData: (id: string, filter: unknown) => ['analytics', 'chart-data', id, JSON.stringify(filter)] as const,
  semantics: ['analytics', 'workspace-semantics'] as const,
};

export function useAvailableTypes() {
  return useQuery({
    queryKey: KEYS.availableTypes,
    queryFn: listAvailableTypes,
    staleTime: 5 * 60 * 1000,
  });
}

export function useComponentCode(chartType: string | null | undefined) {
  return useQuery({
    queryKey: KEYS.componentCode(chartType || ''),
    queryFn: () => getComponentCode(chartType as string),
    enabled: Boolean(chartType),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCharts() {
  return useQuery({ queryKey: KEYS.charts, queryFn: listCharts });
}

/**
 * One chart by id, FROM THE ENDPOINT THAT OWNS ONE (AG-2419).
 *
 * This used to seed itself from the charts list:
 *
 *     initialData: () => qc.getQueryData(KEYS.charts)?.find((c) => c.id === id)
 *
 * which reads like a free head start and is not one. React Query dates
 * `initialData` at now, the client's default staleTime is 30s, so the detail
 * request was never made at all — confirmed live 2026-09-22: opening
 * /charts/{id}/edit issued no GET /charts/{id}. The builder mounted on that
 * row, froze it into `useState` initialisers (it is keyed by chart id, so a
 * later arrival would not re-run them), and Save posts the WHOLE properties
 * object. Whatever the list left out, saving deleted.
 *
 * That was survivable only while the list happened to carry everything. It
 * stopped carrying `_generatedCode` in AG-2417 — 7.6MB of authored component
 * across 1093 charts, which nothing reads from a list — and the same code path
 * would then have silently dropped an agent-built chart back to a seed the
 * first time anyone opened and saved it.
 *
 * A list row is a SUMMARY. All three callers — the builder, the edit overlay
 * and the chart page — want the chart, so none of them may be handed one.
 */
export function useChart(id: string | null | undefined) {
  return useQuery({
    queryKey: ['analytics', 'chart', id || ''],
    queryFn: () => getChart(id as string),
    enabled: Boolean(id),
  });
}

// Re-fetches whenever chartId, the filter context, or row filters change.
export function useChartData(
  chartId: string | null | undefined,
  filterContext: Record<string, unknown>,
  filters: Array<{ field: string; op: string; value: unknown }> = [],
) {
  return useQuery({
    queryKey: KEYS.chartData(chartId || '', { filterContext, filters }),
    queryFn: () => fetchChartData(chartId as string, filterContext, filters),
    enabled: Boolean(chartId),
  });
}

export function useApiSources() {
  return useQuery({
    queryKey: ['analytics', 'api-sources'],
    queryFn: fetchApiSources,
    // Every existing caller wanted the list, and still gets exactly that.
    select: (result) => result.sources,
    // AUTO refresh: keep the source list fresh so APIs deleted in the registry /
    // chatbot (a separate app with its own cache) drop out here quickly. Short
    // stale window + refetch when the tab regains focus / remounts.
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

/**
 * Which families of sources could not be read this time round.
 *
 * Same query, same cache entry, different selector — so asking costs nothing.
 * It exists because an empty list is ambiguous: a workspace with no sources and
 * a workspace whose sources we were refused look identical from the list alone,
 * and the app spent a long time telling people the first when it was the second.
 */
export function useSourceOutages() {
  return useQuery({
    queryKey: ['analytics', 'api-sources'],
    queryFn: fetchApiSources,
    select: (result) => result.unavailable,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

// Register a new API as a data source (used by the Add data source popup).
export function useCreateApiSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiSourceInput) => createRegistryApi(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] }),
  });
}

// Remove a connected source. Charts, dashboards, webhooks and shares could all
// be deleted and a SOURCE could not (AG-2037); the list and every source-derived
// cache are refreshed so a removed one stops being offered anywhere.
export function useDeleteSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => deleteSource(sourceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] });
      qc.invalidateQueries({ queryKey: ['analytics', 'source-meaning'] });
    },
  });
}

// Upload a parsed spreadsheet/JSON as a published Data Studio table source.
export function useCreateDataSheetSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDataSheetInput) => createDataSheetSource(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] }),
  });
}

export function useIntrospect() {
  return useMutation({ mutationFn: (apiId: string) => introspectApi(apiId) });
}

// What a source MEANS. Read on demand (a person opened the tab), and declaring
// part of it re-reads so the provenance badges are right immediately.
export function useSourceMeaning(apiId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'source-meaning', apiId],
    queryFn: () => fetchSourceMeaning(apiId as string),
    enabled: Boolean(apiId) && enabled,
  });
}

export function useDeclareSourceMeaning(apiId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SourceMeaningPatch) => declareSourceMeaning(apiId, patch),
    onSuccess: (data) => {
      qc.setQueryData(['analytics', 'source-meaning', apiId], data);
      // A declared metric becomes the definition every board uses, so anything
      // that reasons about this source is now stale.
      void qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] });
    },
  });
}

// THE WHOLE SEMANTIC LAYER, for the screen that manages it. One query, because
// a metric's usage and its duplicates are only meaningful against every other
// definition in the workspace.
export function useWorkspaceSemantics() {
  return useQuery({
    queryKey: KEYS.semantics,
    queryFn: () => fetchWorkspaceSemantics(),
  });
}

export function useDeclareGlossaryTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (terms: GlossaryTerm[]) => declareGlossaryTerms(terms),
    // A term is how a person's word reaches a build, so re-read rather than
    // patching the cache: the response says how many were written, not what the
    // layer now looks like.
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.semantics }),
  });
}

// Live preview data for an unsaved spec; only runs when `enabled`.
export function usePreviewData(spec: Record<string, unknown>, enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'preview', JSON.stringify(spec)],
    queryFn: () => previewChartData(spec),
    enabled,
  });
}

export function useCreateChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Chart>) => createChart(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.charts }),
  });
}

export function useUpdateChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Chart> }) => updateChart(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.charts }),
  });
}

export function useDeleteChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteChart(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.charts }),
  });
}

// dashboards
export function useDashboards() {
  return useQuery({ queryKey: ['analytics', 'dashboards'], queryFn: listDashboards });
}
export function useDashboard(id: string | null | undefined) {
  return useQuery({
    queryKey: ['analytics', 'dashboard', id || ''],
    queryFn: () => getDashboard(id as string),
    enabled: Boolean(id),
  });
}
export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDashboard(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] }),
  });
}

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Dashboard>) => createDashboard(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] }),
  });
}
export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Dashboard> }) => updateDashboard(id, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] });
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboard', vars.id] });
    },
  });
}

// LLM dashboard auto-layout (returns layout + suggested global filters).
export function useAutoLayoutDashboard() {
  return useMutation({ mutationFn: (chartIds: string[]) => autoLayoutDashboard(chartIds) });
}

// ── sharing ──────────────────────────────────────────────────────────────────
const shareKey = (resourceId: string) => ['analytics', 'shares', resourceId] as const;

export function useShares(resourceId: string | null | undefined) {
  return useQuery({
    queryKey: shareKey(resourceId || ''),
    queryFn: () => listShares(resourceId as string),
    enabled: Boolean(resourceId),
  });
}
export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, resourceId, mode, refreshIntervalSeconds }: { kind: 'chart' | 'dashboard'; resourceId: string; mode?: 'snapshot' | 'live'; refreshIntervalSeconds?: number }) =>
      createShare(kind, resourceId, { mode, refreshIntervalSeconds }),
    onSuccess: (_s, vars) => qc.invalidateQueries({ queryKey: shareKey(vars.resourceId) }),
  });
}
export function useRefreshShare() {
  return useMutation({ mutationFn: (id: string) => refreshShare(id) });
}
export function useDeleteShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; resourceId: string }) => deleteShare(id),
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: shareKey(vars.resourceId) }),
  });
}

// ── webhook (push) data sources ───────────────────────────────────────────────
const WEBHOOKS_KEY = ['analytics', 'webhooks'] as const;

export function useWebhooks() {
  return useQuery({ queryKey: WEBHOOKS_KEY, queryFn: listWebhooks });
}
export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; description?: string; max_rows?: number }) => createWebhook(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
      qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] });
    },
  });
}
export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWebhook(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
      qc.invalidateQueries({ queryKey: ['analytics', 'api-sources'] });
    },
  });
}
export function useRotateWebhookToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rotateWebhookToken(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: WEBHOOKS_KEY }),
  });
}

/**
 * Restyle a saved dashboard from an instruction.
 *
 * Invalidates the board AND the chart list, because the endpoint restyles every
 * tile chart as well as the theme — a cached chart would keep its old palette.
 */
export function useApplyDashboardDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, instruction, logo }: { id: string; instruction: string; logo?: string }) =>
      applyDashboardDesign(id, logo ? { instruction, logo } : { instruction }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboard', vars.id] });
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] });
      qc.invalidateQueries({ queryKey: KEYS.charts });
      qc.invalidateQueries({ queryKey: ['analytics', 'chart-data'] });
    },
  });
}

/** Keep a draft board (built by the chatbot) so it appears in the saved list. */
export function useSaveDashboardDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => saveDashboardDraft(id, name),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] });
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboard', vars.id] });
    },
  });
}

/** One webhook source, with the rows it has buffered. */
export function useWebhook(id: string | null | undefined) {
  return useQuery({
    queryKey: ['analytics', 'webhook', id || ''],
    queryFn: () => getWebhook(id as string),
    enabled: Boolean(id),
  });
}
